// Helper to build share-friendly URLs.
// Strategy:
// 1. If the shop has a custom_domain configured (regardless of activation state)
//    we use it — assumes the negoziante has set up path-forwarding via Aruba/GoDaddy.
// 2. Otherwise we ALWAYS go through the short-link service so the share URL
//    is the cleanest possible (photovendo.com/l/abc123).
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_BASE = (process.env.REACT_APP_PUBLIC_BASE || 'https://photovendo.com').replace(/\/+$/, '');

/**
 * Build a share URL.
 * @param {object} opts
 * @param {object} opts.shop - shop object (must have .slug; optional .custom_domain)
 * @param {string} opts.kind - one of: 'product' | 'flyer' | 'shop'
 * @param {string} opts.id - product id, flyer id (ignored for 'shop')
 * @returns {Promise<string>} share-ready URL
 */
export async function buildShareUrl({ shop, kind, id }) {
  const slug = shop?.slug || '';
  let path = '';
  if (kind === 'product') path = `/prodotto/${id}${slug ? `?shop=${slug}` : ''}`;
  else if (kind === 'flyer') path = `/volantino/${id}${slug ? `?shop=${slug}` : ''}`;
  else if (kind === 'shop') path = slug ? `/negozio/${slug}` : '/';
  else path = '/';

  // 1) If shop has a custom domain set, use it (root + path on the negoziante's domain)
  if (shop?.custom_domain) {
    // Custom domain path-forward: https://casastile.it/{path}
    // The shortest segment depends on kind to keep the URL minimal
    const shortPath = kind === 'product'
      ? `/p/${id}`
      : kind === 'flyer'
      ? `/v/${id}`
      : '/';
    return `https://${shop.custom_domain}${shortPath}`;
  }

  // 2) Fallback: create a short link via API → photovendo.com/l/{code}
  try {
    const fullTarget = `${PUBLIC_BASE}${path}`;
    const { data } = await axios.post(`${API}/short-links`, {
      target_url: fullTarget,
      shop_id: shop?.id || '',
      label: `${kind}:${id || slug}`,
    });
    return data.short_url;
  } catch {
    // Last-resort fallback: full URL
    return `${PUBLIC_BASE}${path}`;
  }
}
